'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { SPARTAN_ENGINE_SCROLL_RELEASED_ATTRIBUTE } from '@/app/lcv/components/sections/spartan/engine/scroll-lock/constants';
import { SECTION_SKIP_COVER_FADE_IN_MS, SECTION_SKIP_COVER_TOTAL_MS } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-reset-veil/config';
import type { SectionEnterSource, SectionGestureInput } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/types';
import type { SectionPlayMode } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/types';
const WHEEL_THRESHOLD = 6;
const TOUCH_THRESHOLD = 12;
const GESTURE_IDLE_MS = 180;
const STEP_COOLDOWN_MS = 400;
const ANIMATION_SAFETY_TIMEOUT_MS = 45000;
const EXIT_SAFETY_TIMEOUT_MS = 3000;
// How long a slide that played its clip in full keeps the viewer on its
// end state before the next step -- or the page release -- is allowed.
export const END_HOLD_MS = 500;

// Going BACKWARDS is a return to something already seen, so it plays only
// the destination's `finalAnimation` beat. Everything else -- the first
// visit, a forward step, a forward bullet jump, and a bullet click on the
// section already showing -- plays in full.
const playModeFor = (from: number, to: number): SectionPlayMode => to < from ? 'final' : 'full';
interface UseVerticalSectionStepperOptions {
  slideCount: number;
  containerRef: RefObject<HTMLDivElement | null>;
  onEnterSlide?: (index: number, playMode: SectionPlayMode, source: SectionEnterSource) => void;
  // A scroll the viewer made while the active slide was still playing, and
  // so was swallowed. Once per gesture, not per wheel tick.
  onBlockedGesture?: (index: number, input: SectionGestureInput) => void;
}
interface UseVerticalSectionStepperResult {
  activeIndex: number;
  exitingIndex: number | null;
  playMode: SectionPlayMode;
  isAnimating: boolean;
  isLocked: boolean;
  isSkipCovered: boolean;
  generation: number[];
  reportAnimationEnd: (index: number) => void;
  reportExitEnd: (index: number) => void;
  goTo: (index: number) => void;
  skip: () => void;
}
export const useVerticalSectionStepper = t0 => {
  const $ = _c(52);
  const {
    slideCount,
    containerRef,
    onEnterSlide,
    onBlockedGesture
  } = t0;
  const lastIndex = slideCount - 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const [playMode, setPlayMode] = useState("full");
  const [isAnimating, setIsAnimating] = useState(true);
  const [isLocked, setIsLocked] = useState(true);
  const [isSkipCovered, setIsSkipCovered] = useState(false);
  const [exitingIndex, setExitingIndex] = useState(null);
  let t1;
  if ($[0] !== slideCount) {
    t1 = () => new Array(slideCount).fill(0);
    $[0] = slideCount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [generation, setGeneration] = useState(t1);
  const activeIndexRef = useRef(activeIndex);
  const playModeRef = useRef(playMode);
  const isAnimatingRef = useRef(isAnimating);
  const isLockedRef = useRef(isLocked);
  const onEnterSlideRef = useRef(onEnterSlide);
  const onBlockedGestureRef = useRef(onBlockedGesture);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = index => {
      activeIndexRef.current = index;
      setActiveIndex(index);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const setActive = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = value => {
      isAnimatingRef.current = value;
      setIsAnimating(value);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const setAnimating = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = value_0 => {
      isLockedRef.current = value_0;
      setIsLocked(value_0);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const setLocked = t4;
  let t5;
  let t6;
  if ($[5] !== onBlockedGesture || $[6] !== onEnterSlide) {
    t5 = () => {
      onEnterSlideRef.current = onEnterSlide;
      onBlockedGestureRef.current = onBlockedGesture;
    };
    t6 = [onEnterSlide, onBlockedGesture];
    $[5] = onBlockedGesture;
    $[6] = onEnterSlide;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  const lastStepAtRef = useRef(0);
  const gestureSpentRef = useRef(false);
  const gestureIdleTimerRef = useRef(null);
  const skipPendingRef = useRef(false);
  const skipSwapTimerRef = useRef(null);
  const skipLowerTimerRef = useRef(null);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (skipSwapTimerRef.current) {
        clearTimeout(skipSwapTimerRef.current);
        skipSwapTimerRef.current = null;
      }
      if (skipLowerTimerRef.current) {
        clearTimeout(skipLowerTimerRef.current);
        skipLowerTimerRef.current = null;
      }
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const clearSkipTimers = t7;
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => () => clearSkipTimers();
    t9 = [clearSkipTimers];
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  const endHoldTimerRef = useRef(null);
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      if (endHoldTimerRef.current) {
        clearTimeout(endHoldTimerRef.current);
        endHoldTimerRef.current = null;
      }
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const clearEndHoldTimer = t10;
  let t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => () => clearEndHoldTimer();
    t12 = [clearEndHoldTimer];
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  useEffect(t11, t12);
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (index_0, mode, source) => {
      lastStepAtRef.current = Date.now();
      clearEndHoldTimer();
      setActive(index_0);
      playModeRef.current = mode;
      setPlayMode(mode);
      setAnimating(true);
      setGeneration(prev => {
        const next = [...prev];
        next[index_0] = (next[index_0] ?? 0) + 1;
        return next;
      });
      onEnterSlideRef.current?.(index_0, mode, source);
    };
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const commitSlide = t13;
  const pendingEntryRef = useRef(null);
  const exitTimerRef = useRef(null);
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const clearExitTimer = t14;
  let t15;
  let t16;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => () => clearExitTimer();
    t16 = [clearExitTimer];
    $[17] = t15;
    $[18] = t16;
  } else {
    t15 = $[17];
    t16 = $[18];
  }
  useEffect(t15, t16);
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      const pending = pendingEntryRef.current;
      if (!pending) {
        return;
      }
      pendingEntryRef.current = null;
      clearExitTimer();
      setExitingIndex(null);
      commitSlide(pending.index, pending.mode, pending.source);
    };
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  const finishExit = t17;
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = index_1 => {
      if (pendingEntryRef.current === null || index_1 !== activeIndexRef.current) {
        return;
      }
      finishExit();
    };
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  const reportExitEnd = t18;
  let t19;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = (index_2, mode_0, withCover, source_0) => {
      clearEndHoldTimer();
      if (!withCover) {
        pendingEntryRef.current = {
          index: index_2,
          mode: mode_0,
          source: source_0
        };
        lastStepAtRef.current = Date.now();
        setAnimating(true);
        setExitingIndex(activeIndexRef.current);
        exitTimerRef.current = setTimeout(finishExit, EXIT_SAFETY_TIMEOUT_MS);
        return;
      }
      pendingEntryRef.current = null;
      clearExitTimer();
      setExitingIndex(null);
      clearSkipTimers();
      skipPendingRef.current = true;
      lastStepAtRef.current = Date.now();
      setAnimating(true);
      setIsSkipCovered(true);
      skipSwapTimerRef.current = setTimeout(() => {
        skipSwapTimerRef.current = null;
        skipPendingRef.current = false;
        commitSlide(index_2, mode_0, source_0);
      }, SECTION_SKIP_COVER_FADE_IN_MS);
      skipLowerTimerRef.current = setTimeout(() => {
        skipLowerTimerRef.current = null;
        setIsSkipCovered(false);
      }, SECTION_SKIP_COVER_TOTAL_MS);
    };
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  const enterSlide = t19;
  let t20;
  if ($[22] !== slideCount) {
    t20 = index_3 => {
      if (index_3 < 0 || index_3 >= slideCount) {
        return;
      }
      setLocked(true);
      enterSlide(index_3, playModeFor(activeIndexRef.current, index_3), true, "bullet");
    };
    $[22] = slideCount;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  const goTo = t20;
  let t21;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      if (skipPendingRef.current || pendingEntryRef.current !== null) {
        return;
      }
      enterSlide(activeIndexRef.current, "final", true, "skip");
    };
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  const skip = t21;
  let t22;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      setLocked(false);
    };
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  const releaseAndDisarm = t22;
  let t23;
  if ($[26] !== lastIndex) {
    t23 = index_4 => {
      if (skipPendingRef.current || pendingEntryRef.current !== null || index_4 !== activeIndexRef.current) {
        return;
      }
      const finish = () => {
        setAnimating(false);
        if (index_4 === lastIndex) {
          releaseAndDisarm();
        }
      };
      if (playModeRef.current !== "full") {
        finish();
        return;
      }
      if (endHoldTimerRef.current) {
        return;
      }
      endHoldTimerRef.current = setTimeout(() => {
        endHoldTimerRef.current = null;
        finish();
      }, END_HOLD_MS);
    };
    $[26] = lastIndex;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  const reportAnimationEnd = t23;
  let t24;
  if ($[28] !== isAnimating || $[29] !== lastIndex) {
    t24 = () => {
      if (!isAnimating) {
        return;
      }
      const timeout = setTimeout(() => {
        setAnimating(false);
        if (activeIndexRef.current === lastIndex) {
          releaseAndDisarm();
        }
      }, ANIMATION_SAFETY_TIMEOUT_MS);
      return () => clearTimeout(timeout);
    };
    $[28] = isAnimating;
    $[29] = lastIndex;
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  let t25;
  if ($[31] !== generation || $[32] !== isAnimating || $[33] !== lastIndex) {
    t25 = [isAnimating, generation, lastIndex, releaseAndDisarm, setAnimating];
    $[31] = generation;
    $[32] = isAnimating;
    $[33] = lastIndex;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[35] !== isLocked) {
    t26 = () => {
      const root = document.documentElement;
      if (isLocked) {
        window.scrollTo({
          top: 0,
          behavior: "instant"
        });
        root.removeAttribute(SPARTAN_ENGINE_SCROLL_RELEASED_ATTRIBUTE);
        return;
      }
      root.setAttribute(SPARTAN_ENGINE_SCROLL_RELEASED_ATTRIBUTE, "");
      return () => root.removeAttribute(SPARTAN_ENGINE_SCROLL_RELEASED_ATTRIBUTE);
    };
    t27 = [isLocked];
    $[35] = isLocked;
    $[36] = t26;
    $[37] = t27;
  } else {
    t26 = $[36];
    t27 = $[37];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[38] !== containerRef || $[39] !== lastIndex) {
    t28 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const isEditableTarget = _temp;
      const isStepBlocked = input => {
        if (!isAnimatingRef.current && !skipPendingRef.current && pendingEntryRef.current === null) {
          return false;
        }
        onBlockedGestureRef.current?.(activeIndexRef.current, input);
        return true;
      };
      const markGestureStreamLive = () => {
        if (gestureIdleTimerRef.current) {
          clearTimeout(gestureIdleTimerRef.current);
        }
        gestureIdleTimerRef.current = setTimeout(() => {
          gestureSpentRef.current = false;
        }, GESTURE_IDLE_MS);
      };
      const step = direction => {
        const current = activeIndexRef.current;
        const target_0 = current + direction;
        if (target_0 < 0) {
          return;
        }
        if (target_0 > lastIndex) {
          releaseAndDisarm();
          return;
        }
        if (Date.now() - lastStepAtRef.current < STEP_COOLDOWN_MS) {
          return;
        }
        enterSlide(target_0, playModeFor(current, target_0), direction < 0, "scroll");
      };
      const handleWheel = event => {
        if (!isLockedRef.current) {
          return;
        }
        event.preventDefault();
        markGestureStreamLive();
        if (Math.abs(event.deltaY) < WHEEL_THRESHOLD || gestureSpentRef.current) {
          return;
        }
        gestureSpentRef.current = true;
        if (isStepBlocked("wheel")) {
          return;
        }
        step(event.deltaY > 0 ? 1 : -1);
      };
      let touchStartY = null;
      let isMultiTouch = false;
      const handleTouchStart = event_0 => {
        isMultiTouch = event_0.touches.length > 1;
        touchStartY = event_0.touches[0]?.clientY ?? null;
        gestureSpentRef.current = false;
      };
      const handleTouchMove = event_1 => {
        if (!isLockedRef.current || isMultiTouch) {
          return;
        }
        event_1.preventDefault();
        if (touchStartY === null || gestureSpentRef.current) {
          return;
        }
        const currentY = event_1.touches[0]?.clientY ?? touchStartY;
        const delta = touchStartY - currentY;
        if (Math.abs(delta) < TOUCH_THRESHOLD) {
          return;
        }
        gestureSpentRef.current = true;
        if (isStepBlocked("touch")) {
          return;
        }
        step(delta > 0 ? 1 : -1);
      };
      const handleTouchEnd = () => {
        touchStartY = null;
        isMultiTouch = false;
        gestureSpentRef.current = false;
      };
      const handleKeyDown = event_2 => {
        if (!isLockedRef.current || event_2.metaKey || event_2.ctrlKey || event_2.altKey || isEditableTarget(event_2.target)) {
          return;
        }
        if (["Home", "End"].includes(event_2.key)) {
          event_2.preventDefault();
          return;
        }
        let direction_0 = null;
        if (["ArrowDown", "PageDown", " "].includes(event_2.key)) {
          direction_0 = 1;
        }
        if (["ArrowUp", "PageUp"].includes(event_2.key)) {
          direction_0 = -1;
        }
        if (direction_0 === null) {
          return;
        }
        event_2.preventDefault();
        if (isStepBlocked("key")) {
          return;
        }
        step(direction_0);
      };
      container.addEventListener("wheel", handleWheel, {
        passive: false
      });
      container.addEventListener("touchstart", handleTouchStart, {
        passive: true
      });
      container.addEventListener("touchmove", handleTouchMove, {
        passive: false
      });
      container.addEventListener("touchend", handleTouchEnd, {
        passive: true
      });
      container.addEventListener("touchcancel", handleTouchEnd, {
        passive: true
      });
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        if (gestureIdleTimerRef.current) {
          clearTimeout(gestureIdleTimerRef.current);
        }
        container.removeEventListener("wheel", handleWheel);
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
        container.removeEventListener("touchend", handleTouchEnd);
        container.removeEventListener("touchcancel", handleTouchEnd);
        window.removeEventListener("keydown", handleKeyDown);
      };
    };
    t29 = [containerRef, lastIndex, enterSlide, releaseAndDisarm];
    $[38] = containerRef;
    $[39] = lastIndex;
    $[40] = t28;
    $[41] = t29;
  } else {
    t28 = $[40];
    t29 = $[41];
  }
  useEffect(t28, t29);
  let t30;
  if ($[42] !== activeIndex || $[43] !== exitingIndex || $[44] !== generation || $[45] !== goTo || $[46] !== isAnimating || $[47] !== isLocked || $[48] !== isSkipCovered || $[49] !== playMode || $[50] !== reportAnimationEnd) {
    t30 = {
      activeIndex,
      exitingIndex,
      playMode,
      isAnimating,
      isLocked,
      isSkipCovered,
      generation,
      reportAnimationEnd,
      reportExitEnd,
      goTo,
      skip
    };
    $[42] = activeIndex;
    $[43] = exitingIndex;
    $[44] = generation;
    $[45] = goTo;
    $[46] = isAnimating;
    $[47] = isLocked;
    $[48] = isSkipCovered;
    $[49] = playMode;
    $[50] = reportAnimationEnd;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  return t30;
};
function _temp(target) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}